import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    DEFAULT_LISTING_HANDLER_EVENT_ERROR_BODY,
    MotorsDraft,
    ListingRequest,
    ListingRequestType,
    ListingResponse,
    LISTING_HANDLER_EVENT_ERROR_TITLE_BOAT,
    LISTING_HANDLER_SUCCESSFULLY,
    REDIRECT_LISTING_PAGE,
    MotorsEditListingRequest,
    MotorsRelistListingRequest
} from '@classifieds-sell/datalayer';
import {
    addTrailingSlash,
    removeTrailingSlash,
    StepDisabledStates
} from '@classifieds-sell/shared';
import {
    MotorsDraftFacade,
    MotorsDraftState,
    IRouterData,
    RouterFacade,
    StepInfo,
    UserFacade
} from '@classifieds-sell/state';
import {
    DEFAULT_NOTIFICATION_DURATION,
    INotificationOptions,
    NotificationService
} from '@classifieds-sell/trademe-core-ui';
import { DelayService } from '@trademe/tangram';
import { from, Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { StepIds } from '../../models/motors-step/motors-step-model';
import { MotorsPageBase } from '../../pages/motors-page-base';
import { environment } from 'apps/motors-sell/src/environments/environment';
import { isEditOrRelistPage } from '@classifieds-sell/shared';
import { FooterService } from 'libs/datalayer/src';
import { ERROR_TITLE } from '@classifieds-sell/trademe-core-ui';
import { REQUIRED_FIELD_ERROR_MESSAGE } from '@classifieds-sell/trademe-core-ui';

@Component({
    selector: 'motors-home',
    templateUrl: './motors-home.component.html',
    styleUrls: ['./motors-home.component.scss'],
    standalone: false
})
export class MotorsHomeComponent implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();
    private _currentPageChange$: Subject<void> = new Subject<void>();
    private _currentPage: MotorsPageBase = null;
    private _draftData: MotorsDraft;
    private _subCategory: string;

    private _routerData: IRouterData;
    private _listingId: number;

    isStepInfoReady = false;
    isFooterVisible = true;

    selectedStepId: StepIds;
    callbackInBeforeStepChange: (arg?: unknown) => void;
    callbackArgInBeforeStepChange?: unknown;

    saveAndExitSubmitted = false;
    beforeContinueSubmitted = false;
    beforeStepChangeSubmitted = false;
    saveAndCompleteLaterSubmitted = false;
    listingEndDate: Date;

    private _isEditOrRelistPage: boolean;

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _draftFacade: MotorsDraftFacade,
        private _router: Router,
        private _routerFacade: RouterFacade,
        private _notificationService: NotificationService,
        private _delayService: DelayService,
        private _userFacade: UserFacade,
        private _footerService: FooterService,
        private _changeDectorRef: ChangeDetectorRef
    ) {}

    ngOnDestroy(): void {
        this._destroyed$.next();
    }

    ngOnInit(): void {
        this._activatedRoute.params.subscribe((params) => {
            this._subCategory = params.subCategory;
            this._listingId = params.listingId;
        });
        this.handleListingResponse();
        from(this._routerFacade.routerData$)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((routerStateData) => {
                this._routerData = routerStateData;
                if (Object.keys(this._routerData.stepInfo).length > 0) {
                    this.isStepInfoReady = true;
                }
            });

        from(this._draftFacade.state$)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((draftStateData) =>
                this.draftStateSubscriber(draftStateData)
            );

        from(this._footerService.footerVisibilityChanged)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((isFooterVisible) => {
                this.isFooterVisible = isFooterVisible;
                this._changeDectorRef.markForCheck();
            });

        this._isEditOrRelistPage = isEditOrRelistPage(
            this._activatedRoute,
            true
        );
    }

    onActivate(page: MotorsPageBase) {
        this._currentPage = page;
        this._currentPageChange$.next();
    }

    onContinue(stepInfo: StepInfo) {
        if (this.isPageValid()) {
            this.selectedStepId = stepInfo.nextStep.stepId as StepIds;

            this.enableNextStepAndUpdateMotorsSteps();

            if (!this._currentPage.dirty()) {
                this._router.navigate([this.selectedStepId], {
                    relativeTo: this._activatedRoute,
                    queryParams: this._activatedRoute.snapshot.queryParams
                });
                return;
            }

            if (this._isEditOrRelistPage) {
                this._currentPage.saveDraft(this._listingId);

                this._router.navigate([this.selectedStepId], {
                    relativeTo: this._activatedRoute,
                    queryParams: this._activatedRoute.snapshot.queryParams
                });
                return;
            }

            if (!this._currentPage.dirty() || this._isEditOrRelistPage) {
                this._router.navigate([this.selectedStepId], {
                    relativeTo: this._activatedRoute,
                    queryParams: this._activatedRoute.snapshot.queryParams
                });
                return;
            }

            this.beforeContinueSubmitted = true;
            this._currentPage.saveDraft();
            this._router.navigate([this.selectedStepId], {
                relativeTo: this._activatedRoute,
                queryParams: this._activatedRoute.snapshot.queryParams
            });
        } else {
            this.displayPageInvalidWarning();
        }
    }

    beforeStepChange(
        cb: (arg?: unknown) => void,
        arg?: unknown,
        isBack?: boolean
    ) {
        const isPageValid = this.isPageValid();
        // Page validation not required when navigating back
        if (isPageValid || isBack) {
            this.callbackInBeforeStepChange = cb;
            this.callbackArgInBeforeStepChange = arg;

            // TODO: isPageValid temporary until we support saving invalid state
            if (
                !this._currentPage.dirty() ||
                !isPageValid ||
                this._isEditOrRelistPage
            ) {
                if (this._isEditOrRelistPage) {
                    this._currentPage.saveDraft(this._listingId);
                }
                this.callbackInBeforeStepChange(
                    this.callbackArgInBeforeStepChange
                );
                return;
            }

            this.beforeStepChangeSubmitted = true;
            this._currentPage.saveDraft();
            this.callbackInBeforeStepChange(this.callbackArgInBeforeStepChange);
            return;
        } else {
            this.displayPageInvalidWarning();
        }
    }

    onCreateListing(categoryId: number) {
        const request: ListingRequest = {
            categoryId,
            type: ListingRequestType.Create
        };
        this._draftFacade.makeListingAction(request);
    }

    onTopUpCompleted() {
        this._userFacade.reloadUserDetails();
        const successNotification: INotificationOptions = {
            title: 'Top up',
            message: 'Top up successfully applied'
        };

        this._notificationService.success(successNotification);
    }

    onEditListing(): void {
        const request: MotorsEditListingRequest = {
            categoryId: this._routerData.categoryId,
            draft: this._draftData,
            listingId: this._listingId,
            listingEndDate: this.listingEndDate,
            type: ListingRequestType.Edit
        };
        this._draftFacade.makeListingAction(request);
    }

    onRelistListing(): void {
        const request: MotorsRelistListingRequest = {
            categoryId: this._routerData.categoryId,
            draft: this._draftData,
            listingId: this._listingId,
            type: ListingRequestType.Relist
        };
        this._draftFacade.makeListingAction(request);
    }

    onSaveAndExit() {
        if (this.isPageValid()) {
            this.saveAndExitSubmitted = true;
            this._currentPage.saveDraft();
            const successNotification: INotificationOptions = {
                title: 'Draft saved',
                message: 'Come back here later to continue your draft'
            };

            this._notificationService.success(successNotification);
            this._delayService
                .createDelay(3500)
                .subscribe(
                    () =>
                        (window.location.href = `${removeTrailingSlash(
                            environment.trademeUrl
                        )}`)
                );
        } else {
            this.displayPageInvalidWarning();
        }
    }

    private handleListingResponse(): void {
        from(this._draftFacade.listingActionResponse$)
            .pipe(takeUntil(this._destroyed$), skip(1)) // ignore past/existing state
            .subscribe((listingResponse) => {
                this.handleResponseListing(
                    listingResponse.response,
                    listingResponse.type
                );
            });
    }

    private handleResponseListing(
        listingResponse: ListingResponse,
        event: string
    ): void {
        if (listingResponse.success) {
            const successNotification: INotificationOptions = {
                title: LISTING_HANDLER_SUCCESSFULLY(event),
                message: REDIRECT_LISTING_PAGE
            };

            this._notificationService.success(successNotification);
            this._delayService
                .createDelay(3500)
                .subscribe(
                    () =>
                        (window.location.href = `${addTrailingSlash(
                            environment.trademeUrl
                        )}${listingResponse.listingId}`)
                );
        } else {
            const errorNotification: INotificationOptions = {
                title: LISTING_HANDLER_EVENT_ERROR_TITLE_BOAT,
                message:
                    (listingResponse.description || '').length > 0
                        ? listingResponse.description
                        : DEFAULT_LISTING_HANDLER_EVENT_ERROR_BODY
            };

            this._notificationService.error(errorNotification);
        }
    }

    private isPageValid(): boolean {
        return this._currentPage.isValid();
    }

    private displayPageInvalidWarning() {
        const errorNotification: INotificationOptions = {
            title: ERROR_TITLE,
            message: REQUIRED_FIELD_ERROR_MESSAGE
        };

        this._notificationService.error(errorNotification);
    }

    private enableNextStepAndUpdateMotorsSteps(): void {
        const temp = this._routerData.steps.map((step) => {
            if (step.stepId === this.selectedStepId) {
                return Object.assign({}, step, {
                    disabledState: StepDisabledStates.enabled
                });
            }

            return step;
        });
        this._routerFacade.updateSteps(temp);
    }

    private draftStateSubscriber(draftStateData: MotorsDraftState): void {
        this._draftData = draftStateData.draftData;
        if (draftStateData.loadingSuccess) {
            if (this.saveAndCompleteLaterSubmitted) {
                this.saveAndCompleteLaterSubmitted = false;
                const successNotification: INotificationOptions = {
                    title: 'Draft saved',
                    message: 'Come back here later to continue your draft'
                };

                this._notificationService.success(successNotification);
                this._delayService
                    .createDelay(3500)
                    .subscribe(
                        () =>
                            (window.location.href = removeTrailingSlash(
                                environment.trademeUrl
                            ))
                    );
            }

            if (this.beforeContinueSubmitted) {
                this.beforeContinueSubmitted = false;
                this._router.navigate([this.selectedStepId], {
                    relativeTo: this._activatedRoute,
                    queryParams: this._activatedRoute.snapshot.queryParams
                });
            }

            if (this.beforeStepChangeSubmitted) {
                this.beforeStepChangeSubmitted = false;
                this.callbackInBeforeStepChange(
                    this.callbackArgInBeforeStepChange
                );
            }
        }

        if (
            draftStateData.loadingFailure &&
            (this.saveAndCompleteLaterSubmitted ||
                this.beforeContinueSubmitted ||
                this.beforeStepChangeSubmitted)
        ) {
            this.saveAndCompleteLaterSubmitted = false;

            const errorNotification: INotificationOptions = {
                title: 'Your draft couldn’t be saved',
                message:
                    draftStateData.error.error?.ErrorDescription ||
                    'Please wait a few seconds and try again.'
            };

            this._notificationService.error(errorNotification);

            this._delayService
                .createDelay(DEFAULT_NOTIFICATION_DURATION)
                .subscribe(() => {
                    this._draftFacade.resetLoadingStatus();
                });
        }
        this.listingEndDate = draftStateData.metadata.listingEndDate;
    }
}
